import { FC, useEffect, useMemo, useState } from 'react';
import { Alert, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from 'processes/auth/model/state';
import { endImpersonation, getImpersonationPayload } from 'shared/helpers/impersonation';

const getSecondsLeft = (exp: number) => Math.max(0, Math.floor(exp - Date.now() / 1000));

const formatTimeLeft = (totalSeconds: number) => {
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const ImpersonationBanner: FC = () => {
	const { t } = useTranslation();
	const { user } = useAuthStore();

	const payload = useMemo(() => getImpersonationPayload(), []);
	const [secondsLeft, setSecondsLeft] = useState(() => (payload ? getSecondsLeft(payload.exp) : 0));

	useEffect(() => {
		if (!payload) return;

		const interval = setInterval(() => {
			const left = getSecondsLeft(payload.exp);
			if (left <= 0) {
				clearInterval(interval);
				alert(t('impersonation.expired'));
				endImpersonation();
				return;
			}
			setSecondsLeft(left);
		}, 1000);

		return () => clearInterval(interval);
	}, [payload]);

	if (!payload) return null;

	return (
		<Alert
			severity={'error'}
			sx={{ marginBottom: '16px', alignItems: 'center' }}
			action={
				<Button color={'inherit'} size={'small'} onClick={() => endImpersonation()}>
					{t('impersonation.end_session')}
				</Button>
			}
		>
			{t('impersonation.banner', {
				user: user?.email ?? payload.sub,
				time: formatTimeLeft(secondsLeft),
			})}
		</Alert>
	);
};

export default ImpersonationBanner;
